<template>
  <div class="congruence-similarity-viz">
    <div class="viz-header-compact" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula-chip" v-latex> \$2\$ </div>
    </div>

    <div class="main-layout vertical">
      <div class="simulation-stage dark-stage">
        <svg viewBox="-200 -200 400 360" class="canvas-svg">
          <!-- Circle -->
          <circle cx="0" cy="0" r="100" fill="rgba(13, 104, 255, 0.1)" stroke="#0D68FF" stroke-width="2" />
          <circle cx="0" cy="0" r="3" fill="#64748b" />
          <text x="-15" y="15" fill="#94a3b8" font-size="14" font-weight="bold">O</text>

          <!-- P Point -->
          <circle :cx="ptP.x" :cy="ptP.y" r="5" fill="#facc15" />
          <text :x="ptP.x + 8" :y="ptP.y - 8" fill="#facc15" font-size="16" font-weight="bold">P</text>

          <g v-if="hasIntersections1">
             <line :x1="ptA.x" :y1="ptA.y" :x2="ptP.x" :y2="ptP.y" stroke="#2AF598" stroke-width="2" />
             <line :x1="ptB.x" :y1="ptB.y" :x2="ptP.x" :y2="ptP.y" stroke="#2AF598" stroke-width="2" />
             <!-- Extend visually behind/forward -->
             <line :x1="ptB.x" :y1="ptB.y" :x2="ptA.x" :y2="ptA.y" stroke="#2AF598" stroke-width="2" stroke-dasharray="3,3" />

             <circle :cx="ptA.x" :cy="ptA.y" r="4" fill="#2AF598" />
             <circle :cx="ptB.x" :cy="ptB.y" r="4" fill="#2AF598" />
             <text :x="ptA.x * 1.15" :y="ptA.y * 1.15 + 5" fill="#2AF598" font-size="14" font-weight="bold" text-anchor="middle">A</text>
             <text :x="ptB.x * 1.15" :y="ptB.y * 1.15 + 5" fill="#2AF598" font-size="14" font-weight="bold" text-anchor="middle">B</text>
          </g>

          <g v-if="hasIntersections2">
             <line :x1="ptC.x" :y1="ptC.y" :x2="ptP.x" :y2="ptP.y" stroke="#f43f5e" stroke-width="2" />
             <line :x1="ptD.x" :y1="ptD.y" :x2="ptP.x" :y2="ptP.y" stroke="#f43f5e" stroke-width="2" />
             <line :x1="ptD.x" :y1="ptD.y" :x2="ptC.x" :y2="ptC.y" stroke="#f43f5e" stroke-width="2" stroke-dasharray="3,3" />

             <circle :cx="ptC.x" :cy="ptC.y" r="4" fill="#f43f5e" />
             <circle :cx="ptD.x" :cy="ptD.y" r="4" fill="#f43f5e" />
             <text :x="ptC.x * 1.15" :y="ptC.y * 1.15" fill="#f43f5e" font-size="14" font-weight="bold" text-anchor="middle">C</text>
             <text :x="ptD.x * 1.15" :y="ptD.y * 1.15" fill="#f43f5e" font-size="14" font-weight="bold" text-anchor="middle">D</text>
          </g>
        </svg>

        <div class="hud-center">
          <div class="status-badge con">
             {{ statusText }}
          </div>
        </div>
      </div>

      <div class="compact-controls">
         <div class="control-card glass">
            <div class="control-vertical-group">
               <div class="param-row">
                  <div class="s-item" style="grid-column: span 2;">
                     <label>游动交点 P (水平跨度 X)</label>
                     <el-slider v-model="ptP_x" :min="-250" :max="250" input-size="mini"></el-slider>
                  </div>
                  <div class="s-item" style="grid-column: span 2;">
                     <label>第一直线旋转角 (控制A,B)</label>
                     <el-slider v-model="angleL1" :min="0" :max="180" input-size="mini"></el-slider>
                  </div>
                  <div class="s-item" style="grid-column: span 2;">
                     <label>游动交点 P (垂直高度 Y)</label>
                     <el-slider v-model="ptP_y" :min="-150" :max="150" input-size="mini"></el-slider>
                  </div>
                  <div class="s-item" style="grid-column: span 2;">
                     <label>第二直线旋转角 (控制C,D)</label>
                     <el-slider v-model="angleL2" :min="0" :max="180" input-size="mini"></el-slider>
                  </div>
               </div>
            </div>
         </div>
         <div class="discovery-card-compact" v-latex>
            <i>🔍</i>
            <p><strong>统一的圆幂：</strong>不管 P 是在圆内相交（形成两段部分），还是在圆外割线（形成内外两段），任何一条穿过圆的线段被 P 分出的两段向外拉乘积 $PA \cdot PB$ 永远相同，值等于 $|OP^2 - R^2|$。</p>
         </div>
      </div>
    </div>
  </div>
</template>

<script>
export default { props: { knowledgePoint: String },
  name: 'PowerOfPointViz',
  data() {
    return {
      ptP_x: 130,
      ptP_y: 60,
      angleL1: 45,
      angleL2: 135
    }
  },
  computed: {
    ptP() { return { x: this.ptP_x, y: this.ptP_y }; },
    isInside() { return (this.ptP_x*this.ptP_x + this.ptP_y*this.ptP_y) < 10000; },
    statusText() {
       if(!this.hasIntersections1 || !this.hasIntersections2) return "（请调整角度穿过圆）";
       if(this.isInside) return "【相交弦定理】 圆内相交，PA·PB = PC·PD （乘积恒等！）";
       return "【圆外割线定理】 圆外相交，全长乘以短段恒等：PA·PB = PC·PD";
    },
    intersections1() { return this.getCircleIntersections(this.ptP, this.angleL1, 100); },
    intersections2() { return this.getCircleIntersections(this.ptP, this.angleL2, 100); },
    hasIntersections1() { return this.intersections1.length > 0; },
    hasIntersections2() { return this.intersections2.length > 0; },
    ptA() { return this.intersections1[0]; },
    ptB() { return this.intersections1[1]; },
    ptC() { return this.intersections2[0]; },
    ptD() { return this.intersections2[1]; }
  },
  methods: {
    getCircleIntersections(p, deg, R) {
      let rad = deg * Math.PI / 180;
      let cos = Math.cos(rad);
      let sin = Math.sin(rad);
      
      let px = p.x, py = p.y;
      // t^2 + 2(px*cos + py*sin)t + (px^2+py^2 - R^2) = 0
      let B = 2 * (px * cos + py * sin);
      let C = px * px + py * py - R * R;
      let delta = B * B - 4 * 1 * C;
      
      if (delta < 0) return [];
      
      let t1 = (-B + Math.sqrt(delta)) / 2;
      let t2 = (-B - Math.sqrt(delta)) / 2;
      
      return [
        { x: px + t1*cos, y: py + t1*sin },
        { x: px + t2*cos, y: py + t2*sin }
      ];
    }
  }
}
</script>

<style scoped lang="scss">
.congruence-similarity-viz { flex: 1; display: flex; flex-direction: column; gap: 8px; height: 100%; overflow: hidden; }
.viz-header-compact { display: flex; justify-content: space-between; align-items: center; padding: 0 4px; .brief { font-size: 13px; color: #1e293b; font-weight: 500; } .formula-chip { background: #f0fdf4; color: #10b981; border: 1px solid #bbf7d0; padding: 4px 14px; border-radius: 20px; font-weight: 700; font-family: monospace; } }
.main-layout { display: flex; gap: 12px; height: 100%; min-height: 0; flex-direction: column; }
.simulation-stage { flex: 1; min-height: 200px; background: #0f172a; border-radius: 12px; border: 1.5px solid #1e293b; position: relative; overflow: hidden; .canvas-svg { width: 100%; height: 100%; } .hud-center { position: absolute; top: 15px; width: 100%; text-align: center; display: flex; justify-content: center; .status-badge { background: rgba(255,255,255,0.1); border: 1px solid rgba(255,255,255,0.2); padding: 4px 16px; border-radius: 20px; color: #fff; font-weight: bold; font-size: 13px; backdrop-filter: blur(4px); &.con { background: rgba(13,104,255,0.2); border-color: #0D68FF; color: #60a5fa; } } } }
.compact-controls { display: flex; flex-direction: column; gap: 8px; }
.control-card { background: #ffffff; padding: 12px 16px; border-radius: 12px; border: 1px solid #e2e8f0; }
.control-vertical-group { display: flex; flex-direction: column; gap: 10px; .param-row { display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px; .s-item { label { font-size: 11px; color: #64748b; font-weight: 700; display: block; margin-bottom: -5px; } } } }
.discovery-card-compact { background: #f8fafc; padding: 10px 14px; border-radius: 10px; border: 1px solid #e2e8f0; display: flex; align-items: center; gap: 10px; i { font-style: normal; font-size: 1.2rem; } p { flex: 1; margin: 0; font-size: 12px; color: #475569; line-height: 1.5; strong { color: #1e293b; } } }
</style>
